/**
 * gsap-animations.js
 * ============================================================
 * Central GSAP animation hub for the theme.
 * Loads GSAP core + ScrollTrigger + Lenis from CDN, then
 * exposes a global `ThemeGSAP` namespace that each section
 * can call.
 *
 * Usage in a section <script>:
 *   document.addEventListener('theme:gsap:ready', function () {
 *     ThemeGSAP.heroParallax('HeroParallax-{{ section.id }}');
 *   });
 * ============================================================
 */

(function () {
  'use strict';

  // ── 0. Design-mode guard ───────────────────────────────────────
  // In the Shopify theme editor, Shopify.designMode is true.
  // We skip all GSAP animations there to avoid conflicts with the
  // editor's iframe scroll container.
  var isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode === true;

  // Detected once at IIFE level so all ThemeGSAP methods can read it.
  var isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);


  // ── 1. Load GSAP + ScrollTrigger + Lenis from CDN ─────────────
  var GSAP_VERSION = '3.12.5';
  var LENIS_VERSION = '1.1.14';
  var CDN_BASE = 'https://cdn.jsdelivr.net/npm/gsap@' + GSAP_VERSION + '/dist/';

  var pending = 3; // gsap + ScrollTrigger + Lenis

  function onScriptLoad() {
    pending -= 1;
    if (pending > 0) return;

    // All three scripts loaded — register ScrollTrigger, init Lenis, fire ready event
    gsap.registerPlugin(ScrollTrigger);

    if (!isDesignMode) {
      // On touch devices (mobile/tablet/iOS Safari) use native scroll.
      // Native scroll is already smooth and optimised by the browser;
      // running Lenis on top introduces iOS Safari glitches and
      // overscroll-behavior conflicts. ScrollTrigger.update() is wired
      // directly to the native scroll event instead.
      if (isTouchDevice) {
        // Native scroll path — no Lenis, no JS-driven smooth scroll.
        // ScrollTrigger reads window.scrollY directly each frame.
        window.addEventListener('scroll', ScrollTrigger.update, { passive: true });
        gsap.ticker.lagSmoothing(0);
        window.ThemeGSAP.lenis = null;
      } else {
        // Desktop path — Lenis smooth scroll wired to GSAP's ticker.
        var lenis = new Lenis();
        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
        gsap.ticker.lagSmoothing(0);
        // Expose so sections can pause/resume if needed (e.g. modal open)
        window.ThemeGSAP.lenis = lenis;
      }
    }

    document.dispatchEvent(new CustomEvent('theme:gsap:ready'));
  }

  function loadScript(src) {
    var script = document.createElement('script');
    script.src = src;
    script.async = false; // preserve load order
    script.onload = onScriptLoad;
    document.head.appendChild(script);
  }

  loadScript(CDN_BASE + 'gsap.min.js');
  loadScript(CDN_BASE + 'ScrollTrigger.min.js');
  loadScript('https://cdn.jsdelivr.net/npm/lenis@' + LENIS_VERSION + '/dist/lenis.min.js');


  // ── 2. ThemeGSAP namespace ─────────────────────────────────────
  window.ThemeGSAP = window.ThemeGSAP || {};


  // ── 3. Utility: strip Dawn native animation classes ───────────
  /**
   * clearNativeClasses(elements)
   * -------------------------------------------------------
   * Removes Shopify Dawn's native scroll-animation classes and
   * any inline opacity/transform styles they may have set.
   * Call this before running GSAP on an element that might have
   * picked up native classes (e.g. from a global selector).
   *
   * @param {Element|NodeList|Array} elements  One or more DOM elements
   */
  var NATIVE_CLASSES = [
    'scroll-trigger',
    'scroll-trigger--offscreen',
    'scroll-trigger--design-mode',
    'scroll-trigger--cancel',
    'animate--fade-in',
    'animate--slide-in',
    'animate--zoom-in',
  ];

  window.ThemeGSAP.clearNativeClasses = function (elements) {
    var els = elements instanceof Element ? [elements] : Array.from(elements);
    els.forEach(function (el) {
      el.classList.remove.apply(el.classList, NATIVE_CLASSES);
      el.style.opacity = '';
      el.style.transform = '';
    });
  };


  // ── 4. Section animations ──────────────────────────────────────

  /**
   * heroParallax(sectionId)
   * -------------------------------------------------------
   * Animates the background image of a Hero Parallax section.
   *
   *   - Background image drifts upward as the section scrolls
   *     past, creating a depth/parallax effect.
   *   - `scrub: true` ties the animation 1-to-1 with scroll.
   *   - The image is oversized in CSS (height: 150%, top: -25%)
   *     to prevent blank edges at the extremes of travel.
   *
   * @param {string} sectionId  The HTML id of the section wrapper
   *                            e.g. 'HeroParallax-shopify-section-xyz'
   */
  window.ThemeGSAP.heroParallax = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    var bgEl = section.querySelector('.hero-parallax__bg-image');
    var subjectEl = section.querySelector('.hero-parallax__subject-image');
    var mediaWrapper = section.querySelector('.hero-parallax__media-wrapper');
    if (!bgEl) return;

    // Respect user motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Strip any accidental native animation classes before GSAP takes over
    ThemeGSAP.clearNativeClasses(bgEl);

    if (mediaWrapper) {
      ThemeGSAP.clearNativeClasses(mediaWrapper);
    }

    // Keep only the scroll parallax motion and remove the load-time zoom.
    // This avoids the first-paint jump while preserving the intended depth
    // effect as the section moves through the viewport.
    var scrub = isTouchDevice ? true : 1;
    gsap.fromTo(
      bgEl,
      { yPercent: -15 },
      {
        yPercent: 15,
        ease: 'none',
        immediateRender: false,
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: scrub,
        },
      }
    );

    if (subjectEl) {
      ThemeGSAP.clearNativeClasses(subjectEl);
      gsap.fromTo(
        subjectEl,
        { yPercent: -4 },
        {
          yPercent: 4,
          ease: 'none',
          immediateRender: false,
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: scrub,
          },
        }
      );
    }
  };

  /**
   * heroAvatars(sectionId)
   * -------------------------------------------------------
   * Animates the avatars in the hero section:
   *   - All avatars start overlapped at the leftmost position
   *   - Each slides to its flex position, staggered left-to-right
   *   - Each scales up as it moves, then returns to normal size
   *
   * @param {string} sectionId  The HTML id of the section wrapper
   */
  window.ThemeGSAP.heroAvatars = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    var avatars = section.querySelectorAll('.hero-parallax__avatar');
    if (!avatars.length) return;

    // Respect user motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    ThemeGSAP.clearNativeClasses(avatars);

    // Get the avatars container (not the social-proof parent)
    var avatarsContainer = avatars[0].parentElement;
    var socialProof = avatarsContainer.parentElement;
    var socialTextWrapper = socialProof.querySelector('.hero-parallax__social-text-wrapper');
    var socialText = socialTextWrapper ? socialTextWrapper.querySelector('.hero-parallax__social-text') : null;
    
    // Hide social text completely to prevent layout shift
    if (socialText) {
      gsap.set(socialText, { autoAlpha: 0 });
    }

    // Avatar dimensions in pixels (from CSS: 32px width, -13px overlap)
    var avatarWidth = 32;  // CSS width in px
    var overlap = 13;      // CSS margin overlap in px
    
    // Spacing between avatar left edges = width - overlap
    var avatarSpacing = avatarWidth - overlap; // 19px

    // Lock dimensions before changing layout to prevent text wrapper from moving below
    var containerWidth = avatarsContainer.offsetWidth;
    var containerHeight = avatarsContainer.offsetHeight;

    // Set avatars container to relative/block for absolute positioning
    // Keep social-proof parent in flex mode
    avatarsContainer.style.position = 'relative';
    avatarsContainer.style.display = 'block';
    avatarsContainer.style.width = containerWidth + 'px';
    avatarsContainer.style.height = containerHeight + 'px';

    // Position all avatars absolutely, stacked at left: 0
    avatars.forEach(function (el, i) {
      el.style.position = 'absolute';
      el.style.left = '0';
      el.style.top = '0';
      el.style.zIndex = 10 + i;
    });

    // Create timeline for precise control
    var tl = gsap.timeline();

    // Staggered slide using transforms (GPU-accelerated, smooth)
    // Top-most avatar (highest z-index) moves first
    tl.to(avatars, {
      x: function (i) { return i * avatarSpacing; },
      duration: 0.6,
      ease: 'power2.out',
      stagger: {
        each: 0.15,
        from: 'end'  // Start from the last avatar (highest z-index, on top)
      }
    });

    // Animate social text in after avatars finish
    if (socialText) {
      tl.to(socialText, {
        autoAlpha: 1,
        duration: 0.5,
        ease: 'power2.out'
      }, '-=0.2');
    }
  };

  /**
   * heroInternalParallax(sectionId)
   * -------------------------------------------------------
   * Isolated parallax animation for the Internal Hero section.
   * Uses internal-specific selectors so it does not affect the
   * original hero parallax section.
   *
   * @param {string} sectionId  The HTML id of the section wrapper
   */
  window.ThemeGSAP.heroInternalParallax = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    var bgEl = section.querySelector('.hero-internal__bg-image');
    var subjectEl = section.querySelector('.hero-internal__subject-image');
    var mediaWrapper = section.querySelector('.hero-internal__media-wrapper');
    if (!bgEl) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    ThemeGSAP.clearNativeClasses(bgEl);

    if (mediaWrapper) {
      ThemeGSAP.clearNativeClasses(mediaWrapper);
      gsap.fromTo(
        mediaWrapper,
        { scale: 1.08 },
        {
          scale: 1,
          duration: 0.6,
          ease: 'cubic-bezier(.16, 1, .3, 1)',
          force3D: true,
        }
      );
    }

    var scrub = isTouchDevice ? true : 1;
    gsap.fromTo(
      bgEl,
      { yPercent: -10 },
      {
        yPercent: 10,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: scrub,
        },
      }
    );

    if (subjectEl) {
      ThemeGSAP.clearNativeClasses(subjectEl);
      gsap.fromTo(
        subjectEl,
        { yPercent: -3 },
        {
          yPercent: 3,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: scrub,
          },
        }
      );
    }
  };

  /**
   * heroInternalAvatars(sectionId)
   * -------------------------------------------------------
   * Avatar reveal animation for the Internal Hero section only.
   *
   * @param {string} sectionId  The HTML id of the section wrapper
   */
  window.ThemeGSAP.heroInternalAvatars = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    var avatars = section.querySelectorAll('.hero-internal__avatar');
    if (!avatars.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    ThemeGSAP.clearNativeClasses(avatars);

    var avatarsContainer = avatars[0].parentElement;
    var socialProof = avatarsContainer.parentElement;
    var socialTextWrapper = socialProof.querySelector('.hero-internal__social-text-wrapper');
    var socialText = socialTextWrapper ? socialTextWrapper.querySelector('.hero-internal__social-text') : null;

    if (socialText) {
      gsap.set(socialText, { autoAlpha: 0 });
    }

    var avatarWidth = 32;
    var overlap = 13;
    var avatarSpacing = avatarWidth - overlap;

    var containerWidth = avatarsContainer.offsetWidth;
    var containerHeight = avatarsContainer.offsetHeight;

    avatarsContainer.style.position = 'relative';
    avatarsContainer.style.display = 'block';
    avatarsContainer.style.width = containerWidth + 'px';
    avatarsContainer.style.height = containerHeight + 'px';

    avatars.forEach(function (el, i) {
      el.style.position = 'absolute';
      el.style.left = '0';
      el.style.top = '0';
      el.style.zIndex = 10 + i;
    });

    var tl = gsap.timeline();

    tl.to(avatars, {
      x: function (i) { return i * avatarSpacing; },
      duration: 0.6,
      ease: 'power2.out',
      stagger: {
        each: 0.15,
        from: 'end'
      }
    });

    if (socialText) {
      tl.to(socialText, {
        autoAlpha: 1,
        duration: 0.5,
        ease: 'power2.out'
      }, '-=0.2');
    }
  };

  /**
   * imageHoverCardsParallax(sectionId)
   * -------------------------------------------------------
   * Parallax scroll effect for the Image Hover Cards section.
   *
   *   - Each card's media wrapper starts shifted down (yPercent: 8)
   *     when the card enters the bottom of the viewport.
   *   - It settles at its natural position (yPercent: 0) once the
   *     card's centre reaches the centre of the screen.
   *   - Animates the .section-image-hover-cards__media wrapper so it
   *     stays completely isolated from the image's hover CSS transform.
   *   - The media wrapper is oversized in CSS (top: -10%, bottom: -10%)
   *     so the drift never exposes the card background at the edges.
   *
   * @param {string} sectionId  The HTML id of the section wrapper
   */
  window.ThemeGSAP.imageHoverCardsParallax = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var cards = section.querySelectorAll('.section-image-hover-cards__card');
    cards.forEach(function (card) {
      var media = card.querySelector('.section-image-hover-cards__media');
      if (!media) return;

      gsap.fromTo(
        media,
        { yPercent: 8 },
        {
          yPercent: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: card,
            start: 'top bottom',
            end: 'center center',
            scrub: isTouchDevice ? true : 1,
          },
        }
      );
    });
  };

  /**
   * featuredProductsParallax(sectionId)
   * -------------------------------------------------------
   * Parallax scroll effect for the Featured Products section.
   *
   *   - Each card's media-inner wrapper starts shifted down (yPercent: 8)
   *     when the card enters the bottom of the viewport.
   *   - It settles at yPercent: 0 once the card's centre hits the
   *     centre of the screen.
   *   - Targets __media-inner (not the image) to stay isolated from
   *     the hover scale on __image/__video.
   *   - __media-inner has negative margins in CSS to oversize it,
   *     so the drift never exposes __media's background.
   *
   * @param {string} sectionId  The HTML id of the section wrapper
   */
  window.ThemeGSAP.featuredProductsParallax = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var cards = section.querySelectorAll('.featured-products-card');
    cards.forEach(function (card) {
      var mediaInner = card.querySelector('.featured-products-card__media-inner');
      if (!mediaInner) return;

      gsap.fromTo(
        mediaInner,
        { yPercent: 8 },
        {
          yPercent: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: card,
            start: 'top bottom',
            end: 'center center',
            scrub: isTouchDevice ? true : 1,
          },
        }
      );
    });
  };

  /**
   * mainArticle(sectionId)
   * -------------------------------------------------------
   * GSAP animation flow for blog article page:
   *   - Clears Dawn native scroll-trigger classes for animated targets
   *   - Reveals header/hero/content on scroll
   *   - Applies a subtle parallax drift to the featured image
   *
   * @param {string} sectionId  The HTML id of the article wrapper
   */
  window.ThemeGSAP.mainArticle = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Clean up previous triggers if section is reloaded in editor
    if (Array.isArray(section.__articleGsapTriggers)) {
      section.__articleGsapTriggers.forEach(function (trigger) {
        if (trigger && typeof trigger.kill === 'function') trigger.kill();
      });
    }
    section.__articleGsapTriggers = [];

    var header = section.querySelector('.article-template__header');
    var hero = section.querySelector('.article-template__hero-container');
    var heroImg = hero ? hero.querySelector('img') : null;
    var content = section.querySelector('[data-article-content]');

    var revealTargets = [header, hero, content].filter(Boolean);
    if (revealTargets.length) {
      ThemeGSAP.clearNativeClasses(revealTargets);
    }

    function addReveal(target, y, duration) {
      if (!target) return;
      var tween = gsap.fromTo(
        target,
        { autoAlpha: 0, y: y },
        {
          autoAlpha: 1,
          y: 0,
          duration: duration,
          ease: 'cubic-bezier(.16, 1, .3, 1)',
          scrollTrigger: {
            trigger: target,
            start: 'top 85%',
            once: true,
          },
        }
      );

      if (tween && tween.scrollTrigger) {
        section.__articleGsapTriggers.push(tween.scrollTrigger);
      }
    }

    addReveal(header, 24, 0.7);
    addReveal(hero, 28, 0.8);
    addReveal(content, 32, 0.9);

    if (hero && heroImg) {
      var parallaxTween = gsap.fromTo(
        heroImg,
        { yPercent: -6 },
        {
          yPercent: 6,
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top bottom',
            end: 'bottom top',
            scrub: isTouchDevice ? true : 1,
          },
        }
      );

      if (parallaxTween && parallaxTween.scrollTrigger) {
        section.__articleGsapTriggers.push(parallaxTween.scrollTrigger);
      }
    }
  };

  /**
   * relatedArticles(sectionId)
   * -------------------------------------------------------
   * Scroll-triggered entrance animations for the Related Articles
   * section on blog post pages:
   *   - Section heading slides up and fades in
   *   - Each card fades and slides up with a staggered delay
   *
   * @param {string} sectionId  The HTML id of the related-articles section
   */
  window.ThemeGSAP.relatedArticles = function (sectionId) {
    if (isDesignMode) return;

    var section = document.getElementById(sectionId);
    if (!section) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var heading = section.querySelector('.related-articles__heading');
    var cards   = section.querySelectorAll('.related-articles__item');

    if (!heading && !cards.length) return;

    // Heading: fade + slide up
    if (heading) {
      ThemeGSAP.clearNativeClasses([heading]);
      gsap.fromTo(
        heading,
        { autoAlpha: 0, y: 28 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.75,
          ease: 'cubic-bezier(.16, 1, .3, 1)',
          scrollTrigger: {
            trigger: heading,
            start: 'top 88%',
            once: true,
          },
        }
      );
    }

    // Cards: staggered fade + slide up
    if (cards.length) {
      ThemeGSAP.clearNativeClasses(cards);
      gsap.fromTo(
        cards,
        { autoAlpha: 0, y: 48 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          ease: 'cubic-bezier(.16, 1, .3, 1)',
          stagger: 0.12,
          scrollTrigger: {
            trigger: cards[0],
            start: 'top 88%',
            once: true,
          },
        }
      );
    }
  };

})();
